import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { pmAdminApi, STATUS_FLOW, PRIORITIES } from '../../lib/pmApi';
import { Siren } from 'lucide-react';
import PoPanel from '../../components/pm/PoPanel';
import JobTimeline from '../../components/pm/JobTimeline';
import AssetContext from '../../components/pm/AssetContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PmServiceRequests = () => {
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ status: '', priority: '', po_filter: '' });
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.po_filter) params.po_filter = filters.po_filter;
    pmAdminApi.requests.list(params).then(setList);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => {
    axios.get(`${API}/admin/employees`, { withCredentials: true }).then(({ data }) => {
      setTechs((data || []).filter((e) => ['technician', 'employee'].includes(e.role)));
    }).catch(() => {});
  }, []);

  const updateStatus = async (id, status) => { await pmAdminApi.requests.updateStatus(id, { status }); load(); };
  const assignTech = async (id, technician_id) => { await pmAdminApi.requests.assignTech(id, { technician_id }); load(); };
  const onPoUpdated = (updated) => setList((cur) => cur.map((r) => r.id === updated.id ? { ...r, ...updated } : r));

  return (
    <div data-testid="admin-pm-requests" className="p-6 max-w-7xl space-y-6">
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MGMT</p>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white">PM Service Requests</h1>
      </div>
      <div className="flex flex-wrap gap-2">
        <select data-testid="admin-pm-req-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
          <option value="">All statuses</option>
          {STATUS_FLOW.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="admin-pm-req-priority" value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
        </select>
        <select data-testid="admin-pm-req-po-filter" value={filters.po_filter} onChange={(e) => setFilters((f) => ({ ...f, po_filter: e.target.value }))} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
          <option value="">All POs</option>
          <option value="missing">Missing PO</option>
          <option value="waiting">Waiting on PM PO</option>
          <option value="received">PO Received</option>
          <option value="rejected">PO Rejected</option>
        </select>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-[#A3A3A3]">
            <tr>
              <th className="p-3 text-left">WO #</th>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Unit</th>
              <th className="p-3 text-left">Priority</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">PO</th>
              <th className="p-3 text-left">Technician</th>
              <th className="p-3 text-left">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <React.Fragment key={r.id}>
                <tr data-testid={`admin-pm-req-row-${r.id}`} className={`border-t border-[#262626] hover:bg-[#1a1a1a] cursor-pointer ${r.priority === 'Emergency' ? 'bg-[#7F1D1D]/10' : ''}`} onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <td className="p-3 text-[#D4AF37] font-mono text-xs">{r.work_order_number}</td>
                  <td className="p-3 text-white">{r.property_name}</td>
                  <td className="p-3 text-[#A3A3A3]">{r.unit_number || '—'}</td>
                  <td className="p-3"><span className={`text-xs font-medium px-2 py-1 rounded ${r.priority === 'Emergency' ? 'bg-[#EF4444]/15 text-[#EF4444]' : 'bg-[#262626] text-[#A3A3A3]'}`}>{r.priority === 'Emergency' && <Siren className="w-3 h-3 inline-block mr-1" />}{r.priority}</span></td>
                  <td className="p-3 text-white text-xs">{r.status}</td>
                  <td className="p-3 text-xs">
                    {r.po_status === 'Not Required' ? <span className="text-[#525252]">N/A</span>
                      : r.po_number ? <span className="text-emerald-300 font-mono">{r.po_number}</span>
                      : r.po_required_at_submit ? <span className="text-[#D4AF37]">Missing</span>
                      : <span className="text-[#525252]">—</span>}
                  </td>
                  <td className="p-3 text-[#A3A3A3]">{r.assigned_technician_name || '—'}</td>
                  <td className="p-3 text-[#525252] text-xs">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
                {open === r.id && (
                  <tr className="bg-[#0A0A0A] border-t border-[#262626]">
                    <td colSpan={8} className="p-4 space-y-3">
                      <p className="text-white text-sm">{r.issue_description}</p>
                      <p className="text-[#A3A3A3] text-xs">Access: {r.access_notes || '—'}</p>
                      <div className="flex flex-wrap gap-2 items-center">
                        <select data-testid={`admin-pm-req-update-status-${r.id}`} value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-white text-xs">
                          {STATUS_FLOW.map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <select data-testid={`admin-pm-req-assign-${r.id}`} defaultValue="" onChange={(e) => e.target.value && assignTech(r.id, e.target.value)} className="bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-white text-xs">
                          <option value="">Assign technician…</option>
                          {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <PoPanel request={r} mode="admin" onUpdated={onPoUpdated} />
                      <AssetContext workOrderId={r.id} onLinked={load} />
                      <JobTimeline workOrderId={r.id} mode="admin" />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#525252]">No PM service requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PmServiceRequests;
